/**
 * Variant Support Types
 * 
 * TypeScript types for variant draft state and variant capability system.
 * These types define the local UI state for variant configuration.
 */

/**
 * Variant mode - how variants are created
 * 
 * Phase 1: Only 'item_attribute' mode is supported.
 * Manufacturer mode will be added in Phase 4.
 */
export type VariantMode = 'item_attribute';

/**
 * Variant draft attribute selection
 * Represents a selected attribute with its selected values
 */
export type VariantDraftAttribute = {
  /** Attribute doc name — may include company suffix (e.g., "Color - CSR") */
  attribute: string;
  /** Clean display name without company suffix (e.g., "Color") */
  displayName?: string;
  /** Selected values for this attribute (e.g., ["S", "M", "L"]) */
  values: string[];
};

/**
 * Variant draft - local UI state for variant configuration
 * This is stored in React state and never persisted until "Save Template & Create Variants"
 */
export type VariantDraft = {
  /** Variant mode */
  mode: VariantMode;
  /** Selected attributes with their values */
  attributes: VariantDraftAttribute[];
  /** Whether to copy template image to all variants */
  useTemplateImage: boolean;
};

/**
 * Validation result for variant draft
 */
export type VariantDraftValidationResult = {
  isValid: boolean;
  errors: string[];
};

/**
 * Default empty variant draft
 */
export function createDefaultVariantDraft(): VariantDraft {
  return {
    mode: 'item_attribute',
    attributes: [],
    useTemplateImage: false,
  };
}
